<template>
    <vue-slider
        ref="slider"
        :style="sliderStyle"
        :value="internalValue"
        :min="0"
        :max="values.length - 1"
        :tooltip-formatter="formatTooltip"
        :marks="formatMarkLabel"
        :direction="direction"
        @change="onInput"
        @dragging="onInput"
    />
</template>

<script>
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/material.css';

import { getNumberFromCssValue, getUnitFromCssValue } from '@/utils/string';

export default {
    name: 'slider',
    components: { VueSlider },

    props: {
        value: {
            type: Number,
            default: 0,
        },

        values: {
            type: Array,
            default() {
                return [];
            },
        },

        direction: {
            type: String,
            default: 'ltr',
        },

        displayUnit: {
            type: String,
            default: '',
        },

        hideMarks: {
            type: Boolean,
            default: false,
        },

        showDecimal: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            internalValue: this.value,
        };
    },

    computed: {
        unitValues() {
            const unit = getUnitFromCssValue(this.values[0]);

            return this.values.map((value) => {
                const number = Number(getNumberFromCssValue(value));

                switch (true) {
                    case unit === '%':
                        return `${ Math.floor(number) }${ this.displayUnit || '%' }`;

                    case unit === 'px':
                        return `${ number }${ this.displayUnit || 'px' }`;

                    case unit === 'rem':
                    case unit === 'em':
                        return `${ number * 16 }${ this.displayUnit || 'px' }`;

                    case !!unit:
                        return `${ number }${ this.displayUnit || unit }`;

                    default:
                        return `${ value }${ this.displayUnit || '' }`;
                }
            });
        },

        sliderStyle() {
            if (this.hideMarks) {
                return {
                    padding: '7px 7px !important',
                };
            }

            return {
                padding: '7px 7px 30px !important',
            };
        },
    },

    watch: {
        value: {
            handler(newValue) {
                this.internalValue = newValue < 0 ? 0 : newValue;
            },
            immediate: true,
        },
    },

    methods: {
        onInput(data) {
            const newValue = typeof data === 'object' ? data.value : data;

            this.internalValue = newValue;
            this.$emit('input', this.internalValue);
        },

        formatTooltip(value) {
            return this.unitValues[value];
        },

        formatMarkLabel(markIndex) {
            if (this.hideMarks) return false;

            const numMarks = this.values.length;
            const firstMark = markIndex === 0;
            const lastMark = markIndex === numMarks - 1;

            const label = {
                label: this.showDecimal
                    ? this.unitValues[markIndex]
                    : this.stripDecimal(this.unitValues[markIndex]),
            };

            const emptyLabel = {
                label: '',
            };

            switch (true) {
                case firstMark:
                case lastMark:
                case numMarks < 8:
                    return label;

                case numMarks < 18: {
                    const markMultiplier = Math.floor(numMarks / 2);

                    if (markIndex % markMultiplier === 0) {
                        return label;
                    }

                    return emptyLabel;
                }

                default: {
                    const markMultiplier = Math.floor(numMarks / 7);

                    if (markIndex % markMultiplier === 0) {
                        const secondToLast = this.isSecondToLastLabel({ markMultiplier, markIndex, numMarks });

                        if (!secondToLast) {
                            return label;
                        }
                    }

                    return emptyLabel;
                }
            }
        },

        // for sliders with a large number of values, we want to prevent the tick mark labels from overlapping each other
        // hiding the second to last tick mark labels helps to prevent this
        isSecondToLastLabel({ markMultiplier, markIndex, numMarks }) {
            const numLabels = Math.floor(numMarks / markMultiplier);
            let secondToLastLabelIndex = ((numLabels - 1) * markMultiplier);

            // if a tick mark label wouldn't naturally display for the last tick
            if (!Number.isInteger(numMarks / markMultiplier)) {
                // we always force the last tick to display a label (better UX)
                // so in this case, the "last" tick mark label we'd naturally display - is now the "second to last" one
                secondToLastLabelIndex += markMultiplier;
            }

            return markIndex === secondToLastLabelIndex;
        },

        stripDecimal(num) {
            if (typeof num === 'string') {
                num = num.replace(/(?:[^-.\d])/g, '');
                num = Number(num);

                if (Number.isNaN(num)) return 0;
            }

            return Math.floor(num);
        },
    },
};
</script>

<style lang="scss">
.vue-slider {
    flex-grow: 1;
}

.vue-slider-rail {
    background-color: var(--form-inputBorderColor);
    border-radius: 15px;
}

.vue-slider-process {
    background-color: var(--form-controlColor);
    border-radius: 0;
    left: -2px !important;
}

.vue-slider-mark {
    &:first-of-type .vue-slider-mark-label {
        left: 5px !important;
    }
}

.vue-slider-mark-label {
    font-size: 11px;
}

.vue-slider-mark-label-active {
    font-weight: 700;
}

.vue-slider-mark-step {
    border-radius: 0;
    width: 1px;
    height: 300%;
    top: -100% !important;
    background-color: var(--app-color);
    opacity: 0.6;
}

.vue-slider-dot-handle {
    background-color:var(--form-controlColor);
}

.vue-slider-dot-handle-focus:after {
    background-color: rgba(74, 144, 226, .38)
}

.vue-slider-dot-tooltip {
    display: none;
}

.vue-slider-dot-tooltip-show {
    display: block;
}

.vue-slider-dot-tooltip-inner {
    background-color: var(--form-controlColor);
}

.vue-slider-dot-tooltip-text {
    width: 40px;
    height: 40px;
}
</style>
